import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectCartTotalBetsCount, selectUnitToken } from '@homeplay/app';
import {
  useAssistantActions,
  useAssistantIntents,
  useAssistantState,
} from '../../hooks/useLottoAssistantContext';
import { useShortcutCooldown } from '../../hooks/useShortcutCooldown';
import useOnboardingTarget from '../../hooks/useOnboardingTarget';
import { CHAT_COMMANDS } from '../../constants';

const mergeRefs = (...refs) => (node) => {
  refs.forEach((ref) => {
    if (!ref) return;
    if (typeof ref === 'function') ref(node);
    else ref.current = node;
  });
};
import allLottoIcon from '../../../../../../shared/assets/images/lotto-assistant/all-lotto.svg';
import lottoResultsIcon from '../../../../../../shared/assets/images/lotto-assistant/lotto-results.svg';
import myBetsIcon from '../../../../../../shared/assets/images/lotto-assistant/my-bets.svg';
import cartIcon from '../../../../../../shared/assets/images/lotto-assistant/cart.svg';
import sendIcon from '../../../../../../shared/assets/images/lotto-assistant/send.svg';
import classes from './ChatInput.module.scss';

const ChatInput = ({ onOpenCart, onOpenMyBets, cartIconRef }) => {
  const [prompt, setPrompt] = useState('');
  const [activeCommandIndex, setActiveCommandIndex] = useState(0);
  const unitToken = useSelector(selectUnitToken);
  const cartCount = useSelector(selectCartTotalBetsCount);
  const inputRef = useRef(null);

  const { isSendingMessage, inputCommand, setInputCommand } =
    useAssistantState();
  const { sendTextMessage } = useAssistantActions();
  const { handleBrowseLotteries, handleLottoResults } = useAssistantIntents();

  const browseLotteriesBtn = useShortcutCooldown();
  const lottoResultsBtn = useShortcutCooldown();

  const chatInputTargetRef = useOnboardingTarget('chat-input');
  const browseLotteriesTargetRef = useOnboardingTarget('browse-lotteries');
  const lottoResultsTargetRef = useOnboardingTarget('lotto-results');
  const myBetsTargetRef = useOnboardingTarget('my-bets');
  const cartTargetRef = useOnboardingTarget('cart');

  useEffect(() => {
    if (inputCommand) inputRef.current?.focus();
  }, [inputCommand]);

  const filteredCommands = CHAT_COMMANDS.filter(({ command }) =>
    command.startsWith(prompt.toLowerCase())
  );
  const showPicker =
    prompt.startsWith('/') && !inputCommand && filteredCommands.length > 0;

  const activePlaceholder =
    CHAT_COMMANDS.find((c) => c.command === inputCommand)?.placeholder ??
    'Type a message...';

  const selectCommand = useCallback(
    (command) => {
      setInputCommand(command);
      setPrompt('');
      setActiveCommandIndex(0);
      inputRef.current?.focus();
    },
    [setInputCommand]
  );

  const submitPrompt = useCallback(async () => {
    if (isSendingMessage || !unitToken) return;
    const text = inputCommand
      ? `${inputCommand} ${prompt.trim()}`
      : prompt.trim();
    if (!text.trim()) return;
    setPrompt('');
    if (inputCommand) setInputCommand(null);
    await sendTextMessage(text);
  }, [
    isSendingMessage,
    unitToken,
    prompt,
    inputCommand,
    setInputCommand,
    sendTextMessage,
  ]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    await submitPrompt();
  };

  const handleKeyDown = async (e) => {
    if (showPicker) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveCommandIndex((i) => (i + 1) % filteredCommands.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveCommandIndex(
          (i) => (i - 1 + filteredCommands.length) % filteredCommands.length
        );
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        selectCommand(filteredCommands[activeCommandIndex].command);
        return;
      }
      if (e.key === 'Escape') {
        setPrompt('');
        return;
      }
    }

    if (e.key === 'Backspace' && !prompt && inputCommand) {
      setInputCommand(null);
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      await submitPrompt();
    }
  };

  const isDisabled = isSendingMessage || !unitToken;
  const submitDisabled = isDisabled || (!prompt.trim() && !inputCommand);

  return (
    <div className={classes.FormWrapper}>
      {showPicker && (
        <div className={classes.CommandPicker}>
          {filteredCommands.map(({ command, description }, index) => (
            <button
              key={command}
              type="button"
              className={`${classes.CommandItem} ${index === activeCommandIndex ? classes.CommandItemActive : ''}`}
              onMouseDown={(e) => {
                e.preventDefault();
                selectCommand(command);
              }}
              onMouseEnter={() => setActiveCommandIndex(index)}
            >
              <span className={classes.CommandIcon} aria-hidden="true">
                /
              </span>
              <div className={classes.CommandTextWrapper}>
                <span className={classes.CommandName}>
                  {command.replace(/^\//, '')}
                </span>
                <span className={classes.CommandDescription}>
                  {description}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}

      <form
        ref={chatInputTargetRef}
        onSubmit={handleSubmit}
        className={classes.InputArea}
      >
        <div className={classes.TextFieldWrapper}>
          {inputCommand && (
            <span className={classes.CommandChip}>
              {inputCommand}
              <button
                type="button"
                className={classes.CommandChipClose}
                onClick={() => {
                  setInputCommand(null);
                  setPrompt('');
                }}
                aria-label="Clear command"
              >
                ×
              </button>
            </span>
          )}
          <input
            ref={inputRef}
            type="text"
            className={classes.TextField}
            value={prompt}
            onChange={(e) => {
              setPrompt(e.target.value);
              setActiveCommandIndex(0);
            }}
            onKeyDown={handleKeyDown}
            placeholder={activePlaceholder}
            disabled={isDisabled}
            maxLength={200}
          />
          <button
            type="submit"
            className={classes.SendButton}
            disabled={submitDisabled}
            aria-label="Send"
          >
            <img src={sendIcon} alt="" />
          </button>
        </div>

        <div className={classes.Shortcuts}>
          <button
            ref={browseLotteriesTargetRef}
            type="button"
            className={classes.ShortcutButton}
            onClick={() =>
              browseLotteriesBtn.execute(() =>
                handleBrowseLotteries({ withSuggestedOptions: true })
              )
            }
            disabled={browseLotteriesBtn.isLoading || isSendingMessage}
            aria-label="Browse lotteries"
          >
            <img src={allLottoIcon} alt="" />
          </button>
          <button
            ref={lottoResultsTargetRef}
            type="button"
            className={classes.ShortcutButton}
            onClick={() => lottoResultsBtn.execute(handleLottoResults)}
            disabled={lottoResultsBtn.isLoading || isSendingMessage}
            aria-label="Lotto results"
          >
            <img src={lottoResultsIcon} alt="" />
          </button>

          <button
            ref={myBetsTargetRef}
            type="button"
            className={classes.ShortcutButton}
            onClick={onOpenMyBets}
            aria-label="My bets"
          >
            <img src={myBetsIcon} alt="" />
          </button>
          <button
            ref={mergeRefs(cartIconRef, cartTargetRef)}
            type="button"
            className={`${classes.ShortcutButton} ${classes.CartButton}`}
            onClick={onOpenCart}
            aria-label="Cart"
          >
            <img src={cartIcon} alt="" />
            {cartCount > 0 && (
              <span className={classes.CartBadge}>{cartCount}</span>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ChatInput;
